import { Component, computed, effect, OnInit, signal } from '@angular/core';
import { AppModule } from "../app.module";
import { PricingService } from './pricing.service';
import { PricingRuleStockItem, PricingRuleStockCustomItem, PricingTemplate, PricingTemplateStockCustomItem, PricingTemplateStockItem, PricingTemplateStockList } from '../models/pricing';
import { NotificationService } from '../shared/notification.service';
import { TyreLocation } from '../shared/enums';
import { ManualAddStockItem } from './manual-add-stock-item/manual-add-stock-item';
import { SharedDataService } from '../shared/shareddata.service';
import { AuthService } from '../auth.service';

@Component({
  selector: 'app-pricing',
  imports: [AppModule, ManualAddStockItem],
  templateUrl: './pricing.html',
  styleUrl: './pricing.less'
})
export class Pricing{

  pricingTemplates = signal<PricingTemplate[]>([]);
  selectedPricingTemplateStockList = signal<PricingTemplateStockList>({stockItems: [], stockCustomItems: []}); 
  selectedStockItem = signal<PricingTemplateStockItem | null>(null);
  selectedStockCustomItem = signal<PricingTemplateStockCustomItem | null>(null);
  selectedIndex = signal<number>(0);
  isLoading = signal<boolean>(false);
  selectedBrandIds = signal<number[]>([]);
  selectedSizeIds = signal<string[]>([]);
  stockSearchTerm = signal<string>('');
  stockCustomSearchTerm = signal<string>('');
  isManualAddVisible = signal<boolean>(false);

filteredStockItems = computed(() => {
    const searchTerm = this.stockSearchTerm().toLowerCase();
    const brands = this.selectedBrandIds();
    const sizes = this.selectedSizeIds();
    const stockList = this.selectedPricingTemplateStockList();
    const stockItems = stockList.stockItems;
    let items = stockItems;

    if (sizes.length > 0) {
        items = items.filter(item => sizes.includes(item.genCode.toString()));
    }

    if (brands.length > 0) {
        items = items.filter(item => brands.includes(item.tyreBrandId));
    }

    if (searchTerm && searchTerm.trim() !== '' && searchTerm.length >= 2) {
        items = items.filter(item =>
            item.description.toLowerCase().includes(searchTerm) ||
            item.stockCode.toLowerCase().includes(searchTerm)
        );
    }

    return items;
})

  filteredStockCustomItems = computed(() => {
    const searchTerm = this.stockCustomSearchTerm().toLowerCase();
    const stockCustomItemsList = this.selectedPricingTemplateStockList();
    const stockCustomItems = stockCustomItemsList.stockCustomItems;
    let items = stockCustomItems;
    if (searchTerm && searchTerm.trim() !== '' && searchTerm.length >= 2) {
      items = items.filter(item =>
        item.description.toLowerCase().includes(searchTerm)
      );
    }
    return items;
  })

  selectedTemplate = computed(() => {
    const templates = this.pricingTemplates();
    const index = this.selectedIndex();
    if(templates.length > 0 && index >= 0 && index < templates.length) {
      return templates[index];
    }
    return null;
  })

  listofBrandsFilter = computed(() => {
    const brands = this.sharedData.tyreBrands;
    return brands.map(brand => ({ value: brand.tyreBrandId, text: brand.brandName })).sort((a, b) => a.text.localeCompare(b.text));
  })

  listOfSizesFilter = computed(() => {
    const sizes = this.sharedData.tyreSizes;
    return sizes.map(size => ({ value: size.gencode.toString(), text: size.size })).sort((a, b) => a.text.localeCompare(b.text));
  })

  isTemplateUnsaved = computed(() => {
    const template = this.selectedTemplate();
    return template && template.pricingTemplateId === 0;
  });

  constructor(private pricingService: PricingService, private notification: NotificationService, private sharedData: SharedDataService, public auth: AuthService) {
    this.fetchInitialTemplates();

  effect(() => {
      const template = this.selectedTemplate();
      this.stockSearchTerm.set('');
      this.stockCustomSearchTerm.set('');
      this.selectedBrandIds.set([]);
      this.selectedSizeIds.set([]);

      if (template && template.pricingTemplateId) {
        this.getSelectedTemplateStockList(template.pricingTemplateId);
      } else {
        this.selectedPricingTemplateStockList.set({stockItems: [], stockCustomItems: []});
      }
    })

   }

  private fetchInitialTemplates(): void {
    this.isLoading.set(true);
    this.pricingService.getPricingTemplates().subscribe({
      next: (templates: PricingTemplate[]) => {
        this.pricingTemplates.set(templates);
        this.isLoading.set(false);
      },
      error: (err) => {
        this.notification.handleError(err, "Error fetching pricing templates");
        this.isLoading.set(false);
      }
    });
  } 

  private getSelectedTemplateStockList(templateId: number): void {
    console.log("Fetching stock list for template ID:", templateId);
    this.isLoading.set(true);
    this.pricingService.getPricingTemplateStockList(templateId).subscribe({
          next: (stockList: PricingTemplateStockList) => {    
            console.log("Received stock list for template ID:", templateId, stockList);
            this.selectedPricingTemplateStockList.set(stockList);
            console.log("Updated selectedPricingTemplateStockList:", this.selectedPricingTemplateStockList());
          },
          error: (err) => {
            this.notification.handleError(err, "Error fetching pricing template stock list");
          },
          complete: () => {
            this.isLoading.set(false);
          }
        });
  }

  public addPricingTemplate(): void {
    const newTemplate: PricingTemplate = {
      pricingTemplateId: 0,
      clientId: 0,
      pricingName: 'New Template',
      description: '',
      pricingMethod: 3, // Default to Cost Plus % method
      pricingValue: 0
    };
    this.pricingTemplates.update(templates => [...templates, newTemplate])
    this.selectedIndex.set(this.pricingTemplates().length - 1);
  }

  public savePricingTemplate(template: PricingTemplate): void {
    this.isLoading.set(true);
    const wasNewTemplate = template.pricingTemplateId === 0;
    this.pricingService.savePricingTemplate(template).subscribe({
      next: (savedTemplate: PricingTemplate) => {
        this.notification.showSuccess("Pricing template saved successfully");
        this.pricingTemplates.update(templates => 
        templates.map(t => t === template ? savedTemplate : t)
      );
      
       if (wasNewTemplate) {
        this.getSelectedTemplateStockList(savedTemplate.pricingTemplateId);
      }
      },
      error: (err) => {
        this.notification.handleError(err, "Error saving pricing template");
      },
      complete: () => {
        this.isLoading.set(false);
      }
    });
  }

  public removeTemplate(templateId: number): void {
    this.isLoading.set(true);

    
    this.pricingService.removePricingTemplate(templateId).subscribe({
      next: () => {
        this.notification.showSuccess("Pricing template removed successfully");
        this.pricingTemplates.set(this.pricingTemplates().filter(t => t.pricingTemplateId !== templateId));
        this.selectedIndex.set(0);
        this.isLoading.set(false);
      },
      error: (err) => {
        this.notification.handleError(err, "Error removing pricing template");
        this.isLoading.set(false);
      }
    });
  }

  public savePricingRule(item: PricingTemplateStockItem, value: number): void {
    this.isLoading.set(true);
    const numericValue = Math.round(value * 100) / 100;
    
    const newRule : PricingRuleStockItem = {
      pricingRuleId: item.pricingRule ? item.pricingRule.pricingRuleId : 0,
      pricingTemplateId: this.pricingTemplates()[this.selectedIndex()].pricingTemplateId,
      pricingMethod: 1, // Fixed price pricing method
      pricingValue: numericValue,
      tyreBrandId: item.tyreBrandId,
      msfId: item.msfId
  }
  if ( (!item.pricingRule && newRule.pricingValue !== item.price) || (item.pricingRule && newRule.pricingValue !== item.pricingRule.pricingValue)) {
      this.pricingService.savePricingRule(newRule).subscribe({
        next: (savedRule) => {
          this.notification.showSuccess("Pricing rule saved successfully");
          this.isLoading.set(false);
            item.pricingRule = savedRule;
          this.selectedStockItem.set(null);
        },
        error: (err) => {
          this.notification.handleError(err, "Error saving pricing rule");
          this.isLoading.set(false);
        }
      });
  } else {
    this.notification.showInfo("No changes to save");
    this.isLoading.set(false);
    this.selectedStockItem.set(null);
  }
  }

  public removePricingRule(item: PricingTemplateStockItem): void {
    this.isLoading.set(true);
    if(item.pricingRule && item.pricingRule.pricingRuleId) {
      this.pricingService.deletePricingRule(item.pricingRule.pricingRuleId).subscribe({
        next: () => {
          this.notification.showSuccess("Pricing rule deleted successfully");
          item.pricingRule = null;
          this.isLoading.set(false);
        },
        error: (err) => {
          this.notification.handleError(err, "Error deleting pricing rule");
          this.isLoading.set(false);
        }
      });
    }
  }

  public saveStockCustomPricingRule(item: PricingTemplateStockCustomItem, value: number): void {
    this.isLoading.set(true);
    const numericValue = Math.round(value * 100) / 100;
    
    const newRule : PricingRuleStockCustomItem = {
      pricingRuleId: item.pricingRule ? item.pricingRule.pricingRuleId : 0,
      pricingTemplateId: this.pricingTemplates()[this.selectedIndex()].pricingTemplateId,
      pricingMethod: 1, // Fixed price pricing method
      pricingValue: numericValue,
      stockCustomId: item.stockCustomId
    };
    if ( (!item.pricingRule && newRule.pricingValue !== item.price) || (item.pricingRule && newRule.pricingValue !== item.pricingRule.pricingValue)) {
      this.pricingService.saveStockCustomPricingRule(newRule).subscribe({
        next: (savedRule) => {
          this.notification.showSuccess("Stock custom pricing rule saved successfully");
          this.isLoading.set(false);
            item.pricingRule = savedRule;
          this.selectedStockCustomItem.set(null);
        },
        error: (err) => {
          this.notification.handleError(err, "Error saving stock custom pricing rule");
          this.isLoading.set(false);
        }
      });
    } else {
      this.notification.showInfo("No changes to save");
      this.isLoading.set(false);
      this.selectedStockCustomItem.set(null);
    }
  }

  public removeStockCustomPricingRule(item: PricingTemplateStockCustomItem): void {
    this.isLoading.set(true);
    if(item.pricingRule && item.pricingRule.pricingRuleId) {
      this.pricingService.deleteStockCustomPricingRule(item.pricingRule.pricingRuleId).subscribe({
        next: () => {
          this.notification.showSuccess("Stock custom pricing rule deleted successfully");
          item.pricingRule = null;
          this.isLoading.set(false);
        },
        error: (err) => {
          this.notification.handleError(err, "Error deleting stock custom pricing rule");
          this.isLoading.set(false);
        }
      });
    }
    else {
      this.notification.showInfo("No pricing rule to delete");
      this.isLoading.set(false);
    }
  }

  public tyreType(tyreLocation: TyreLocation): string {
    switch (tyreLocation) {
      case TyreLocation.steer:
        return "Steer";
      case TyreLocation.drive:
        return "Drive";
      case TyreLocation.spare:
        return "Spare";
      default:
        return "Drive/Steer";
    }
  }

  public clearFilters(): void {
    this.selectedBrandIds.set([]);
    this.selectedSizeIds.set([]);
  }

  public clearSearch(): void {
      this.stockSearchTerm.set('');
  }

  public clearCustomSearch(): void {
      this.stockCustomSearchTerm.set('');
  }

  public activeFilterCount = computed(() => {
      return this.selectedBrandIds().length + this.selectedSizeIds().length;
  });

  get userClientTypes() : number[] {
    return this.auth.user?.clientTypes || [];
  }
}
